import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const PDF_WATERMARK_TEXT = "Check Guard";
const PDF_CAPTURE_PIXEL_RATIO = 2;
const PDF_PAGED_CAPTURE_PIXEL_RATIO = 1.6;
const PDF_PAGED_IMAGE_TYPE = "JPEG";
const PDF_PAGED_IMAGE_MIME_TYPE = "image/jpeg";
const PDF_PAGED_IMAGE_QUALITY = 0.86;
const PDF_IMAGE_COMPRESSION = "FAST";
const DEFAULT_SINGLE_PDF_LAYOUT = {
  marginX: 10,
  marginTop: 30,
  marginBottom: 10,
  centerSinglePage: false,
};
const DEFAULT_PAGED_PDF_LAYOUT = {
  marginX: 6,
  marginTop: 12,
  marginBottom: 8,
  footerHeight: 14,
  centerContentVertically: false,
};
const PAGE_NUMBER_BASE_CANVAS_WIDTH = 1305;
const PAGE_NUMBER_BASE_FONT_SIZE = 28;
const PAGE_NUMBER_BASE_BOTTOM_OFFSET = 28;

export const REPORT_PRINT_PDF_LAYOUT = {
  single: {
    marginX: 14,
    marginTop: 20,
    marginBottom: 16,
    centerSinglePage: true,
  },
  paged: {
    marginX: 6,
    marginTop: 12,
    marginBottom: 10,
    footerHeight: 12,
    centerContentVertically: true,
    captureScale: PDF_PAGED_CAPTURE_PIXEL_RATIO,
    imageQuality: PDF_PAGED_IMAGE_QUALITY,
  },
};

export default function PdfDownloadBtn({ ref, file_name, t = "Download PDF", layout }) {
  const clearFileName = file_name.slice(0, file_name.lastIndexOf("."));

  const handleDownLoadPdf = () => {
    createPdfReport(ref, clearFileName, layout);
  };
  return (
    <button className="btn-bg-blue" onClick={() => handleDownLoadPdf()}>
      {t}
    </button>
  );
}

function emitPdfProgress(onProgress, payload) {
  if (typeof onProgress !== "function") return;
  onProgress(payload);
}

export async function createPdfReport(ref, clearFileName, layout = {}, onProgress) {
  const input = ref.current;
  if (!input) return;

  emitPdfProgress(onProgress, {
    stage: "preparing",
    percent: 10,
    current: 0,
    total: 1,
  });

  const restoreImages = await prepareImagesForPdfCapture(input);
  let canvas;
  try {
    canvas = await html2canvas(input, {
      scale: PDF_CAPTURE_PIXEL_RATIO,
      backgroundColor: null,
    });
  } finally {
    restoreImages();
  }
  emitPdfProgress(onProgress, {
    stage: "capturing",
    percent: 42,
    current: 1,
    total: 1,
  });
  const croppedCanvas = cropCanvasToContent(canvas);
  applyWatermarkToCanvas(croppedCanvas);
  emitPdfProgress(onProgress, {
    stage: "rendering",
    percent: 74,
    current: 1,
    total: 1,
  });

  const imgData = croppedCanvas.toDataURL("image/png");
  const pdf = new jsPDF("p", "mm", "a4");
  const resolvedLayout = {
    ...DEFAULT_SINGLE_PDF_LAYOUT,
    ...(layout || {}),
  };

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = resolvedLayout.marginX;
  const marginTop = resolvedLayout.marginTop;
  const marginBottom = resolvedLayout.marginBottom;
  const usableWidth = pageWidth - marginX * 2;
  const pageSliceHeight = pageHeight - marginTop - marginBottom;
  const imgHeight = (croppedCanvas.height * usableWidth) / croppedCanvas.width;

  let heightLeft = imgHeight;
  let offsetY = 0;
  const initialY =
    resolvedLayout.centerSinglePage && imgHeight <= pageSliceHeight
      ? marginTop + (pageSliceHeight - imgHeight) / 2
      : marginTop;

  pdf.addImage(
    imgData,
    "PNG",
    marginX,
    initialY,
    usableWidth,
    imgHeight,
    undefined,
    PDF_IMAGE_COMPRESSION,
  );

  heightLeft -= pageSliceHeight;

  while (heightLeft > 0) {
    offsetY += pageSliceHeight;
    pdf.addPage();
    pdf.addImage(
      imgData,
      "PNG",
      marginX,
      marginTop - offsetY,
      usableWidth,
      imgHeight,
      undefined,
      PDF_IMAGE_COMPRESSION,
    );
    heightLeft -= pageSliceHeight;
  }

  emitPdfProgress(onProgress, {
    stage: "saving",
    percent: 94,
    current: 1,
    total: 1,
  });
  await savePdfDocument(pdf, `${clearFileName}.pdf`);
  emitPdfProgress(onProgress, {
    stage: "complete",
    percent: 100,
    current: 1,
    total: 1,
  });
}

export async function createPagedPdfReport(ref, clearFileName, layout = {}, onProgress) {
  const input = ref.current;

  if (!input) return;

  const pageElements = Array.from(input.children).filter(
    (element) => element instanceof HTMLElement,
  );

  if (!pageElements.length) return;

  emitPdfProgress(onProgress, {
    stage: "preparing",
    percent: 8,
    current: 0,
    total: pageElements.length,
  });

  const pdf = new jsPDF("p", "mm", "a4");
  const resolvedLayout = {
    ...DEFAULT_PAGED_PDF_LAYOUT,
    ...(layout || {}),
  };
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = resolvedLayout.marginX;
  const marginTop = resolvedLayout.marginTop;
  const footerHeight = resolvedLayout.footerHeight;
  const marginBottom = resolvedLayout.marginBottom;
  const usableWidth = pageWidth - marginX * 2;
  const totalRenderHeight = pageHeight - marginTop - marginBottom;
  const captureScale = getPagedPdfCaptureScale(
    pageElements.length,
    resolvedLayout.captureScale,
  );
  const imageQuality = getPdfImageQuality(resolvedLayout.imageQuality);
  let reportPageNumber = 0;

  for (let index = 0; index < pageElements.length; index += 1) {
    const pageElement = pageElements[index];
    const isCoverPage = pageElement.dataset.reportCover === "true";
    const hidePageNumber =
      pageElement.dataset.reportPageNumberHidden === "true";
    let restoreImages = () => {};
    let canvas = null;
    let croppedCanvas = null;
    let pageCanvas = null;

    if (!hidePageNumber) {
      reportPageNumber += 1;
    }

    try {
      restoreImages = await prepareImagesForPdfCapture(pageElement);
      canvas = await html2canvas(pageElement, {
        scale: captureScale,
        backgroundColor: null,
      });
      croppedCanvas = cropCanvasToContent(canvas);
      pageCanvas = buildPagedPdfCanvas(croppedCanvas, {
        usableWidth,
        totalRenderHeight,
        footerHeight,
        pageNumber: hidePageNumber ? null : reportPageNumber,
        applyWatermark: !isCoverPage,
        centerContentVertically: resolvedLayout.centerContentVertically,
      });
      if (index > 0) {
        pdf.addPage();
      }

      const pageImageData = await canvasToPdfImageData(
        pageCanvas,
        PDF_PAGED_IMAGE_MIME_TYPE,
        imageQuality,
      );

      pdf.addImage(
        pageImageData,
        PDF_PAGED_IMAGE_TYPE,
        marginX,
        marginTop,
        usableWidth,
        totalRenderHeight,
        undefined,
        PDF_IMAGE_COMPRESSION,
      );

      emitPdfProgress(onProgress, {
        stage: "page",
        percent: 14 + Math.round(((index + 1) / pageElements.length) * 76),
        current: index + 1,
        total: pageElements.length,
      });
    } finally {
      restoreImages();
      releaseCanvas(pageCanvas);
      if (croppedCanvas !== canvas) {
        releaseCanvas(croppedCanvas);
      }
      releaseCanvas(canvas);
    }

    await waitForNextFrame();
  }

  emitPdfProgress(onProgress, {
    stage: "saving",
    percent: 96,
    current: pageElements.length,
    total: pageElements.length,
  });
  await savePdfDocument(pdf, `${clearFileName}.pdf`);
  emitPdfProgress(onProgress, {
    stage: "complete",
    percent: 100,
    current: pageElements.length,
    total: pageElements.length,
  });
}

function getPagedPdfCaptureScale(pageCount, explicitScale) {
  const adaptiveScale =
    pageCount >= 60
      ? 1.15
      : pageCount >= 35
        ? 1.25
        : pageCount >= 20
          ? 1.4
          : PDF_PAGED_CAPTURE_PIXEL_RATIO;
  const numericScale = Number(explicitScale);
  if (Number.isFinite(numericScale) && numericScale > 0) {
    return Math.min(numericScale, adaptiveScale);
  }

  return adaptiveScale;
}

function getPdfImageQuality(explicitQuality) {
  const numericQuality = Number(explicitQuality);
  if (!Number.isFinite(numericQuality)) return PDF_PAGED_IMAGE_QUALITY;
  return Math.min(1, Math.max(0.65, numericQuality));
}

async function savePdfDocument(pdf, fileName) {
  const result = pdf.save(fileName, { returnPromise: true });

  if (result && typeof result.then === "function") {
    await result;
  }
}

function waitForNextFrame() {
  if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve, reject) => {
    if (!canvas) {
      reject(new Error("Cannot export an empty canvas."));
      return;
    }

    if (typeof canvas.toBlob === "function") {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to convert PDF page canvas to an image blob."));
          }
        },
        mimeType,
        quality,
      );
      return;
    }

    try {
      const dataUrl = canvas.toDataURL(mimeType, quality);
      const binary = atob(dataUrl.split(",")[1] || "");
      const bytes = new Uint8Array(binary.length);

      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
      }

      resolve(new Blob([bytes], { type: mimeType }));
    } catch (error) {
      reject(error);
    }
  });
}

function blobToArrayBuffer(blob) {
  if (typeof blob.arrayBuffer === "function") {
    return blob.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

async function canvasToPdfImageData(canvas, mimeType, quality) {
  const blob = await canvasToBlob(canvas, mimeType, quality);
  const arrayBuffer = await blobToArrayBuffer(blob);

  return new Uint8Array(arrayBuffer);
}

function releaseCanvas(canvas) {
  if (!canvas) return;

  canvas.width = 0;
  canvas.height = 0;
}

function isDataOrBlobUrl(value) {
  return (
    typeof value === "string" &&
    (value.trim().startsWith("data:") || value.trim().startsWith("blob:"))
  );
}

function isFetchableImageSource(value) {
  if (typeof value !== "string") return false;

  const trimmed = value.trim();
  return (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("/")
  );
}

async function imageUrlToDataUrl(url) {
  const response = await fetch(url, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`image fetch failed: ${response.status}`);
  }

  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () =>
      resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function waitForImageLoad(image) {
  if (image.complete && image.naturalWidth > 0) return Promise.resolve();

  return new Promise((resolve) => {
    const done = () => {
      image.removeEventListener("load", done);
      image.removeEventListener("error", done);
      resolve();
    };

    image.addEventListener("load", done, { once: true });
    image.addEventListener("error", done, { once: true });
  });
}

async function prepareImagesForPdfCapture(root) {
  const images = Array.from(root.querySelectorAll("img"));
  const restores = [];

  await Promise.all(
    images.map(async (image) => {
      const originalSrc = image.getAttribute("src");
      const originalSrcSet = image.getAttribute("srcset");
      const source = image.currentSrc || image.src || originalSrc || "";

      if (!source || isDataOrBlobUrl(source) || !isFetchableImageSource(source)) {
        return;
      }

      try {
        const dataUrl = await imageUrlToDataUrl(source);
        if (!dataUrl) return;

        restores.push(() => {
          if (originalSrc == null) {
            image.removeAttribute("src");
          } else {
            image.setAttribute("src", originalSrc);
          }

          if (originalSrcSet == null) {
            image.removeAttribute("srcset");
          } else {
            image.setAttribute("srcset", originalSrcSet);
          }
        });

        image.removeAttribute("srcset");
        image.setAttribute("src", dataUrl);
        await waitForImageLoad(image);
      } catch (error) {
        console.warn("[PdfDownloadBtn] image conversion failed:", {
          src: source,
          message: error?.message,
        });
      }
    }),
  );

  return () => {
    restores.forEach((restore) => restore());
  };
}

function cropCanvasToContent(canvas) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height).data;

  let left = width;
  let right = -1;
  let top = height;
  let bottom = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      const alpha = imageData[index + 3];

      if (alpha === 0) continue;

      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }

  if (right < left || bottom < top) {
    return canvas;
  }

  if (left === 0 && top === 0 && right === width - 1 && bottom === height - 1) {
    return canvas;
  }

  const croppedWidth = right - left + 1;
  const croppedHeight = bottom - top + 1;
  const croppedCanvas = document.createElement("canvas");
  const croppedCtx = croppedCanvas.getContext("2d");

  croppedCanvas.width = croppedWidth;
  croppedCanvas.height = croppedHeight;
  croppedCtx.drawImage(
    canvas,
    left,
    top,
    croppedWidth,
    croppedHeight,
    0,
    0,
    croppedWidth,
    croppedHeight,
  );

  return croppedCanvas;
}

function applyWatermarkToCanvas(canvas) {
  const ctx = canvas.getContext("2d");
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  ctx.save();
  const watermarkScale = Math.max(canvasWidth / PAGE_NUMBER_BASE_CANVAS_WIDTH, 1);
  const watermarkFontSize = Math.round(60 * watermarkScale);
  ctx.font = `bold ${watermarkFontSize}px sans-serif`;
  ctx.fillStyle = "rgba(180, 180, 180, 0.3)";
  ctx.translate(canvasWidth / 2, canvasHeight / 2);
  ctx.rotate((-45 * Math.PI) / 180);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(PDF_WATERMARK_TEXT, 0, 0);
  ctx.restore();
}

function buildPagedPdfCanvas(
  sourceCanvas,
  {
    usableWidth,
    totalRenderHeight,
    footerHeight,
    pageNumber,
    applyWatermark,
    centerContentVertically,
  },
) {
  const pageCanvas = document.createElement("canvas");
  const pageCanvasWidth = sourceCanvas.width;
  const pageCanvasHeight = Math.round(
    (pageCanvasWidth * totalRenderHeight) / usableWidth,
  );
  const footerHeightPx = Math.round((pageCanvasWidth * footerHeight) / usableWidth);
  const contentHeightPx = Math.max(pageCanvasHeight - footerHeightPx, 1);
  const scale = Math.min(
    pageCanvasWidth / sourceCanvas.width,
    contentHeightPx / sourceCanvas.height,
  );
  const renderWidth = sourceCanvas.width * scale;
  const renderHeight = sourceCanvas.height * scale;
  const offsetX = (pageCanvasWidth - renderWidth) / 2;
  const offsetY = centerContentVertically
    ? Math.max((contentHeightPx - renderHeight) / 2, 0)
    : 0;
  const ctx = pageCanvas.getContext("2d");

  pageCanvas.width = pageCanvasWidth;
  pageCanvas.height = pageCanvasHeight;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, pageCanvasWidth, pageCanvasHeight);
  ctx.drawImage(sourceCanvas, offsetX, offsetY, renderWidth, renderHeight);

  if (applyWatermark) {
    applyWatermarkToCanvas(pageCanvas);
  }
  drawCanvasPageNumber(ctx, pageCanvasWidth, pageCanvasHeight, pageNumber);

  return pageCanvas;
}

function drawCanvasPageNumber(ctx, canvasWidth, canvasHeight, pageNumber) {
  if (pageNumber == null) return;

  const pageNumberScale = canvasWidth / PAGE_NUMBER_BASE_CANVAS_WIDTH;
  const fontSize = Math.max(
    16,
    Math.round(PAGE_NUMBER_BASE_FONT_SIZE * pageNumberScale),
  );
  const bottomOffset = Math.max(
    16,
    Math.round(PAGE_NUMBER_BASE_BOTTOM_OFFSET * pageNumberScale),
  );

  ctx.save();
  ctx.fillStyle = "#475569";
  ctx.font = `700 ${fontSize}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(` - ${pageNumber} - `, canvasWidth / 2, canvasHeight - bottomOffset);
  ctx.restore();
}
